/**
 * Cubic Bézier curve type for the MD3 Expressive Shape Engine.
 * Ported from androidx.graphics.shapes.Cubic.kt
 *
 * A cubic holds 8 floats: [anchor0X, anchor0Y, control0X, control0Y, control1X, control1Y, anchor1X, anchor1Y]
 */

import type { PointTransformer } from "../utils/utils";
import { DISTANCE_EPSILON, interpolate, square } from "../utils/utils";
import { type Point, point } from "./point";

/**
 * A single cubic Bézier curve segment.
 *
 * The curve goes from anchor0 → (via control0, control1) → anchor1.
 *
 * @example
 * const line = Cubic.straightLine(0, 0, 1, 0);
 * const arc  = Cubic.circularArc(0, 0, 1, 0, 0, 1);
 */
export class Cubic {
	/** Internal storage: [a0x, a0y, c0x, c0y, c1x, c1y, a1x, a1y] */
	readonly points: number[];

	constructor(points: number[]) {
		if (points.length !== 8)
			throw new Error("Cubic points array must have exactly 8 elements");
		this.points = points;
	}

	/** First anchor point X */
	get anchor0X(): number {
		return this.points[0];
	}
	/** First anchor point Y */
	get anchor0Y(): number {
		return this.points[1];
	}
	/** First control point X */
	get control0X(): number {
		return this.points[2];
	}
	/** First control point Y */
	get control0Y(): number {
		return this.points[3];
	}
	/** Second control point X */
	get control1X(): number {
		return this.points[4];
	}
	/** Second control point Y */
	get control1Y(): number {
		return this.points[5];
	}
	/** Second anchor point X */
	get anchor1X(): number {
		return this.points[6];
	}
	/** Second anchor point Y */
	get anchor1Y(): number {
		return this.points[7];
	}

	/**
	 * Returns a point on the curve at parameter t (0=start, 1=end).
	 * Uses the standard cubic Bézier formula.
	 * @param t - Parameter in [0, 1]
	 */
	pointOnCurve(t: number): Point {
		const u = 1 - t;
		return point(
			this.anchor0X * (u * u * u) +
				this.control0X * (3 * t * u * u) +
				this.control1X * (3 * t * t * u) +
				this.anchor1X * (t * t * t),
			this.anchor0Y * (u * u * u) +
				this.control0Y * (3 * t * u * u) +
				this.control1Y * (3 * t * t * u) +
				this.anchor1Y * (t * t * t),
		);
	}

	/**
	 * Returns true if this curve has (near) zero length.
	 */
	zeroLength(): boolean {
		return (
			Math.abs(this.anchor0X - this.anchor1X) < DISTANCE_EPSILON &&
			Math.abs(this.anchor0Y - this.anchor1Y) < DISTANCE_EPSILON
		);
	}

	/**
	 * Splits this cubic at parameter t, returning two cubics.
	 * Uses De Casteljau's algorithm.
	 * @param t - Split parameter in [0, 1]
	 * @returns [left, right] pair
	 */
	split(t: number): [Cubic, Cubic] {
		const u = 1 - t;
		const mid = this.pointOnCurve(t);
		const left = new Cubic([
			this.anchor0X,
			this.anchor0Y,
			this.anchor0X * u + this.control0X * t,
			this.anchor0Y * u + this.control0Y * t,
			this.anchor0X * (u * u) +
				this.control0X * (2 * u * t) +
				this.control1X * (t * t),
			this.anchor0Y * (u * u) +
				this.control0Y * (2 * u * t) +
				this.control1Y * (t * t),
			mid.x,
			mid.y,
		]);
		const right = new Cubic([
			mid.x,
			mid.y,
			this.control0X * (u * u) +
				this.control1X * (2 * u * t) +
				this.anchor1X * (t * t),
			this.control0Y * (u * u) +
				this.control1Y * (2 * u * t) +
				this.anchor1Y * (t * t),
			this.control1X * u + this.anchor1X * t,
			this.control1Y * u + this.anchor1Y * t,
			this.anchor1X,
			this.anchor1Y,
		]);
		return [left, right];
	}

	/**
	 * Returns a reversed copy of this cubic (anchor0 ↔ anchor1, controls swapped).
	 */
	reverse(): Cubic {
		return new Cubic([
			this.anchor1X,
			this.anchor1Y,
			this.control1X,
			this.control1Y,
			this.control0X,
			this.control0Y,
			this.anchor0X,
			this.anchor0Y,
		]);
	}

	/**
	 * Calculates the axis-aligned bounding box.
	 * @param approximate - If true, uses control point hull (faster). Default: false.
	 * @returns [minX, minY, maxX, maxY]
	 */
	calculateBounds(approximate = false): [number, number, number, number] {
		if (this.zeroLength()) {
			return [this.anchor0X, this.anchor0Y, this.anchor0X, this.anchor0Y];
		}

		let minX = Math.min(this.anchor0X, this.anchor1X);
		let minY = Math.min(this.anchor0Y, this.anchor1Y);
		let maxX = Math.max(this.anchor0X, this.anchor1X);
		let maxY = Math.max(this.anchor0Y, this.anchor1Y);

		if (approximate) {
			minX = Math.min(minX, this.control0X, this.control1X);
			minY = Math.min(minY, this.control0Y, this.control1Y);
			maxX = Math.max(maxX, this.control0X, this.control1X);
			maxY = Math.max(maxY, this.control0Y, this.control1Y);
			return [minX, minY, maxX, maxY];
		}

		// Find derivative roots (quadratic formula) for x
		const xa =
			-this.anchor0X + 3 * this.control0X - 3 * this.control1X + this.anchor1X;
		const xb = 2 * this.anchor0X - 4 * this.control0X + 2 * this.control1X;
		const xc = -this.anchor0X + this.control0X;

		const expandX = (t: number) => {
			if (t >= 0 && t <= 1) {
				const v = this.pointOnCurve(t).x;
				if (v < minX) minX = v;
				if (v > maxX) maxX = v;
			}
		};

		if (Math.abs(xa) < DISTANCE_EPSILON) {
			if (xb !== 0) expandX((2 * xc) / (-2 * xb));
		} else {
			const xs = xb * xb - 4 * xa * xc;
			if (xs >= 0) {
				expandX((-xb + Math.sqrt(xs)) / (2 * xa));
				expandX((-xb - Math.sqrt(xs)) / (2 * xa));
			}
		}

		// Same for y
		const ya =
			-this.anchor0Y + 3 * this.control0Y - 3 * this.control1Y + this.anchor1Y;
		const yb = 2 * this.anchor0Y - 4 * this.control0Y + 2 * this.control1Y;
		const yc = -this.anchor0Y + this.control0Y;

		const expandY = (t: number) => {
			if (t >= 0 && t <= 1) {
				const v = this.pointOnCurve(t).y;
				if (v < minY) minY = v;
				if (v > maxY) maxY = v;
			}
		};

		if (Math.abs(ya) < DISTANCE_EPSILON) {
			if (yb !== 0) expandY((2 * yc) / (-2 * yb));
		} else {
			const ys = yb * yb - 4 * ya * yc;
			if (ys >= 0) {
				expandY((-yb + Math.sqrt(ys)) / (2 * ya));
				expandY((-yb - Math.sqrt(ys)) / (2 * ya));
			}
		}

		return [minX, minY, maxX, maxY];
	}

	/**
	 * Interpolates between this cubic and another at fraction t.
	 * @param other - Target cubic
	 * @param t - Fraction [0, 1]
	 */
	interpolateTo(other: Cubic, t: number): Cubic {
		return new Cubic(
			this.points.map((v, i) => interpolate(v, other.points[i], t)),
		);
	}

	/**
	 * Applies a PointTransformer to all anchor and control points.
	 * @param fn - The transform function
	 */
	transformed(fn: PointTransformer): Cubic {
		const p = this.points.slice();
		for (let i = 0; i < 8; i += 2) {
			const [tx, ty] = fn(p[i], p[i + 1]);
			p[i] = tx;
			p[i + 1] = ty;
		}
		return new Cubic(p);
	}

	toString(): string {
		return `Cubic(a0=(${this.anchor0X},${this.anchor0Y}) c0=(${this.control0X},${this.control0Y}) c1=(${this.control1X},${this.control1Y}) a1=(${this.anchor1X},${this.anchor1Y}))`;
	}

	// ─── Static factories ─────────────────────────────────────────────────────

	/**
	 * Creates a cubic representing a straight line.
	 * Control points lie at 1/3 and 2/3 of the line.
	 * @param x0 - Start anchor X
	 * @param y0 - Start anchor Y
	 * @param x1 - End anchor X
	 * @param y1 - End anchor Y
	 */
	static straightLine(x0: number, y0: number, x1: number, y1: number): Cubic {
		return new Cubic([
			x0,
			y0,
			interpolate(x0, x1, 1 / 3),
			interpolate(y0, y1, 1 / 3),
			interpolate(x0, x1, 2 / 3),
			interpolate(y0, y1, 2 / 3),
			x1,
			y1,
		]);
	}

	/**
	 * Creates a cubic approximating a circular arc.
	 * p0 and p1 must be equidistant from the center.
	 * For arcs > 180°, use multiple cubics.
	 *
	 * @param centerX - Arc center X
	 * @param centerY - Arc center Y
	 * @param x0 - Start point X (on circle)
	 * @param y0 - Start point Y (on circle)
	 * @param x1 - End point X (on circle)
	 * @param y1 - End point Y (on circle)
	 */
	static circularArc(
		centerX: number,
		centerY: number,
		x0: number,
		y0: number,
		x1: number,
		y1: number,
	): Cubic {
		const p0dx = x0 - centerX;
		const p0dy = y0 - centerY;
		const p1dx = x1 - centerX;
		const p1dy = y1 - centerY;
		const d0 = Math.sqrt(p0dx * p0dx + p0dy * p0dy);
		const d1 = Math.sqrt(p1dx * p1dx + p1dy * p1dy);
		if (d0 < DISTANCE_EPSILON || d1 < DISTANCE_EPSILON) {
			return Cubic.straightLine(x0, y0, x1, y1);
		}
		// Normalized direction vectors
		const nx0 = p0dx / d0,
			ny0 = p0dy / d0;
		const nx1 = p1dx / d1,
			ny1 = p1dy / d1;
		// 90-degree rotations (tangent directions)
		const rx0 = -ny0,
			ry0 = nx0;
		const rx1 = -ny1,
			ry1 = nx1;
		// cos of angle between vectors
		const cosa = nx0 * nx1 + ny0 * ny1;
		if (cosa > 0.999) return Cubic.straightLine(x0, y0, x1, y1);
		// Is arc clockwise?
		const clockwise = rx0 * p1dx + ry0 * p1dy >= 0;
		const k =
			((d0 *
				(4 / 3) *
				(Math.sqrt(2 * (1 - cosa)) - Math.sqrt(1 - square(cosa)))) /
				(1 - cosa)) *
			(clockwise ? 1 : -1);
		return new Cubic([
			x0,
			y0,
			x0 + rx0 * k,
			y0 + ry0 * k,
			x1 - rx1 * k,
			y1 - ry1 * k,
			x1,
			y1,
		]);
	}

	/**
	 * Creates a zero-length cubic at the given point.
	 * @param x - X coordinate
	 * @param y - Y coordinate
	 */
	static empty(x: number, y: number): Cubic {
		return new Cubic([x, y, x, y, x, y, x, y]);
	}
}

/**
 * A mutable version of Cubic used in performance-critical paths (Morph.forEachCubic).
 * Reuses the same instance to avoid allocations.
 */
export class MutableCubic extends Cubic {
	constructor() {
		super([0, 0, 0, 0, 0, 0, 0, 0]);
	}

	/**
	 * Mutably interpolates between c1 and c2 at the given progress.
	 * @param c1 - Start cubic
	 * @param c2 - End cubic
	 * @param progress - Interpolation factor [0, 1]
	 */
	interpolate(c1: Cubic, c2: Cubic, progress: number): void {
		for (let i = 0; i < 8; i++) {
			(this.points as number[])[i] = interpolate(
				c1.points[i],
				c2.points[i],
				progress,
			);
		}
	}

	/**
	 * Mutably applies a PointTransformer to all points.
	 * @param fn - The transform function
	 */
	transform(fn: PointTransformer): void {
		const p = this.points as number[];
		for (let i = 0; i < 8; i += 2) {
			const [tx, ty] = fn(p[i], p[i + 1]);
			p[i] = tx;
			p[i + 1] = ty;
		}
	}
}
